import { motion } from "framer-motion";
import {
  CreditCard,
  ShieldCheck,
  Bitcoin,
  GitBranch,
  Send,
  LineChart,
  ScrollText,
  ArrowRight,
} from "lucide-react";
import Navbar from "../components/neptune/Navbar";
import Footer from "../components/neptune/Footer";
import PageHeader from "../components/neptune/PageHeader";
import FinalCTA from "../components/neptune/FinalCTA";
import { useContent } from "../context/ContentContext";

const ICONS = {
  Gateway: CreditCard,
  Acquiring: ShieldCheck,
  Crypto: Bitcoin,
  Routing: GitBranch,
  Payouts: Send,
  Analytics: LineChart,
  Settlement: ScrollText,
};

const fallbackSolutions = [
  {
    Icon: CreditCard,
    tag: "Gateway",
    title: "A unified payment gateway.",
    body: "One integration covering cards, wallets, bank transfers, open banking and 80+ alternative methods. Built for global checkout from day one.",
    bullets: [
      "Tokenization & 3DS2 native",
      "Multi-currency presentment",
      "Smart fields and saved methods",
    ],
  },
  {
    Icon: ShieldCheck,
    tag: "Acquiring",
    title: "Direct card acquiring, optimized.",
    body: "Process directly with regional acquirers to maximize approval rates and minimize cost. Failover and redundancy come standard.",
    bullets: [
      "Local acquiring across 60+ markets",
      "MID strategies for risk diversification",
      "Network tokenization built in",
    ],
  },
  {
    Icon: Bitcoin,
    tag: "Crypto",
    title: "Crypto without the risk.",
    body: "Accept stablecoins and major assets at checkout, settle to fiat in your bank account. You get the conversion, we manage the rails.",
    bullets: [
      "USDC, USDT, BTC, ETH, SOL",
      "Instant on-chain settlement",
      "No custody, no volatility exposure",
    ],
  },
  {
    Icon: GitBranch,
    tag: "Routing",
    title: "Smart routing in milliseconds.",
    body: "Each transaction is evaluated for cost, latency and approval probability across your stack — and routed to the optimal path automatically.",
    bullets: [
      "Real-time decisioning engine",
      "Cascading retries on soft declines",
      "ML-driven optimization",
    ],
  },
  {
    Icon: Send,
    tag: "Payouts",
    title: "Mass payouts, anywhere.",
    body: "Disburse to vendors, partners and customers in 60+ currencies with built-in compliance, FX and beneficiary verification.",
    bullets: [
      "Bulk and on-demand transfers",
      "SWIFT, SEPA, ACH and local rails",
      "Programmable schedules",
    ],
  },
  {
    Icon: LineChart,
    tag: "Analytics",
    title: "Real-time intelligence.",
    body: "Every payment metric in one place — across PSPs, methods, geographies and customer cohorts. Built for finance and product teams alike.",
    bullets: [
      "Approval-rate breakdowns",
      "Cohort & funnel analytics",
      "Exportable reports & APIs",
    ],
  },
  {
    Icon: ScrollText,
    tag: "Settlement",
    title: "Reconciled to the cent.",
    body: "Automated daily settlement, fee decomposition and reconciliation. End-of-month closes that take minutes — not weeks.",
    bullets: [
      "Multi-currency settlement accounts",
      "Granular fee transparency",
      "Accounting integrations",
    ],
  },
];

export default function Solutions() {
  const { t } = useContent();
  const solutions = t("solutions.items", fallbackSolutions);
  return (
    <main
      data-testid="solutions-page"
      className="relative min-h-screen bg-[#0a0a0a] text-white font-body overflow-x-hidden"
    >
      <Navbar />

      <PageHeader
        eyebrow={t("solutions.header.eyebrow", "Solutions")}
        title={t("solutions.header.title", "A modular suite. One orchestration.")}
        sub={t("solutions.header.sub", "Each Neptune product is best-in-class on its own — and exponentially more powerful when used together.")}
      />

      <section className="relative py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
            {solutions.map((s, i) => {
              const Icon = ICONS[s.tag] || CreditCard;
              return (
              <motion.article
                key={s.tag || i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                data-testid={`solution-${(s.tag || "").toLowerCase()}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-y-6 gap-x-12 py-16 sm:py-24 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                {/* Visual / number */}
                <div className="lg:col-span-5">
                  <div className="flex items-baseline gap-4">
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                      {String(i + 1).padStart(2, "0")} · {s.tag}
                    </div>
                  </div>
                  <div className="mt-6 relative h-44 sm:h-56 rounded-2xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]">
                    {/* Pulsing aura */}
                    <motion.div
                      className="absolute -inset-4 bg-[radial-gradient(circle_at_30%_30%,rgba(0,85,255,0.35),transparent_60%)]"
                      animate={{ opacity: [0.45, 0.85, 0.45], scale: [1, 1.06, 1] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Slow rotating conic ring */}
                    <motion.div
                      aria-hidden="true"
                      className="absolute inset-6 rounded-full opacity-40"
                      style={{
                        background:
                          "conic-gradient(from 0deg, rgba(0,229,255,0) 0%, rgba(0,229,255,0.6) 25%, rgba(0,85,255,0) 50%)",
                        mask: "radial-gradient(circle, transparent 56%, black 57%, black 60%, transparent 61%)",
                        WebkitMask:
                          "radial-gradient(circle, transparent 56%, black 57%, black 60%, transparent 61%)",
                      }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
                    />
                    {/* Floating icon */}
                    <motion.div
                      className="relative h-full flex items-center justify-center"
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 4 }}
                        transition={{ type: "spring", stiffness: 300, damping: 18 }}
                      >
                        <Icon
                          className="h-14 w-14 text-white/85 drop-shadow-[0_0_20px_rgba(0,85,255,0.4)]"
                          strokeWidth={1.2}
                        />
                      </motion.div>
                    </motion.div>
                  </div>
                </div>

                {/* Text */}
                <div className="lg:col-span-7">
                  <h2 className="font-display text-3xl sm:text-5xl lg:text-[56px] lg:leading-[1.05] font-semibold tracking-tighter text-white">
                    {s.title}
                  </h2>
                  <p className="mt-6 text-[15.5px] sm:text-[17px] text-zinc-400 leading-relaxed max-w-xl">
                    {s.body}
                  </p>
                  <ul className="mt-7 space-y-2.5 max-w-md">
                    {(s.bullets || []).map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[14px] text-zinc-300"
                      >
                        <span className="mt-2 h-1 w-3 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#0055FF]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
              );
            })}
          </div>

          <div className="mt-20 flex items-center gap-2 text-[13px] text-zinc-500">
            <span>Have a specific use case?</span>
            <a
              href="/contact"
              className="text-white hover:text-[#00E5FF] inline-flex items-center gap-1 transition-colors"
              data-testid="solutions-contact-link"
            >
              Talk to our team <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
}
